import { Box, Stack } from '@mui/system';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { useTheme } from '@mui/material/styles';
import WaveSurfer from 'wavesurfer.js';

import { LoadingButton } from '@mui/lab';
import { Button, Typography } from '@mui/material';
import { addSeconds } from 'date-fns';
import { Timer } from '../Timer';
import Iconify from '../iconify/Iconify';

export type AudioPlayerRef = {
  end(time: number): void;
  seek(time: number): void;
  playPause(): void;
  play(): void;
  pause(): void;
};

type AudioPlayerProps = {
  src: string;
} & (
  | {
      audioDuration: number;
      streaming: true;
    }
  | {
      audioDuration?: undefined;
      streaming?: undefined;
    }
);

const speedOptions = [1, 1.5, 2];

export const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(
  ({ src, audioDuration, streaming }, ref) => {
    const theme = useTheme();
    const [isPlaying, setIsPlaying] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [timer, setTimer] = useState('');
    const [speed, setSpeed] = useState(1);
    const [endTime, setEndTime] = useState<number | null>();
    const [currentTime, setCurrentTime] = useState<number>();

    const containerRef = useRef<HTMLDivElement>(null);
    const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);

    const playPause = () => {
      if (!wavesurfer) return;
      setIsPlaying(!isPlaying);
      wavesurfer?.setPlaybackRate(speed);
      wavesurfer?.playPause();
    };

    const calculateTimer = useCallback(() => {
      if (!wavesurfer) return;
      const currentTime = +wavesurfer.getCurrentTime().toFixed(0);
      const duration = +wavesurfer.getDuration().toFixed(0);

      const time = (duration - currentTime) * (1 / speed);

      setTimer(addSeconds(new Date(), time).toISOString());
    }, [speed, wavesurfer]);

    useEffect(() => {
      calculateTimer();
    }, [calculateTimer]);

    const changeSpeed = () => {
      const index = speedOptions.indexOf(speed);
      const nextSpeed = (index + 1) % speedOptions.length;
      setSpeed(speedOptions[nextSpeed]);
      wavesurfer?.setPlaybackRate(speedOptions[nextSpeed], true);
    };

    useEffect(() => {
      if (!containerRef.current) return () => {};

      const wave = WaveSurfer.create({
        container: containerRef.current,
        waveColor: theme.palette.grey[400],
        progressColor: theme.palette.primary.main,
        height: 24,
        barHeight: 4,
        cursorWidth: 0,
        hideScrollbar: true,
        audioRate: 1,
        ...(!streaming
          ? {
              barGap: 4,
              barRadius: 1,
              barWidth: 2,
            }
          : {}),
      });

      wave.on('dblclick', () => {
        wave.playPause();
      });

      wave.on('finish', () => {
        setIsPlaying(false);
      });

      if (streaming) {
        setIsReady(true);
      } else {
        wave.on('ready', () => {
          setIsReady(true);
        });

        wave.on('loading', () => {
          setIsReady(false);
        });
      }

      wave.on('timeupdate', (currentTime: number) => {
        setCurrentTime(currentTime);
      });

      setWavesurfer(wave);

      return () => {
        wave?.destroy();
        setWavesurfer(null);
      };
    }, [theme.palette.grey, theme.palette.primary.main, streaming]);

    const loadWaveSurfer = useCallback(() => {
      if (!wavesurfer || !src) return;

      if (streaming) {
        wavesurfer.load(src, [new Array(400).fill(0.05)], audioDuration);
        setTimer(addSeconds(new Date(), audioDuration).toISOString());
        return;
      }

      wavesurfer.load(src);

      const sub = wavesurfer.on('ready', () => {
        setTimer(
          addSeconds(new Date(), wavesurfer.getDuration()).toISOString(),
        );

        sub();
      });
    }, [src, wavesurfer, streaming, audioDuration]);

    useEffect(() => {
      // For some reason, the wavesurfer was stucking on loading state
      loadWaveSurfer();
    }, [loadWaveSurfer]);

    useEffect(() => {
      if (!wavesurfer) return;

      return wavesurfer.on('seeking', () => {
        calculateTimer();
      });
    }, [wavesurfer, calculateTimer]);

    useEffect(() => {
      if (!wavesurfer || !isPlaying || !endTime || !currentTime) return;
      if (currentTime >= endTime) {
        setIsPlaying(false);
        wavesurfer?.pause();
      }
    }, [currentTime, endTime, isPlaying, wavesurfer]);

    useImperativeHandle(ref, () => ({
      end: (time) => {
        setEndTime(time);
      },
      seek: (time) => {
        if (!wavesurfer) return;
        const duration = +wavesurfer.getDuration().toFixed(0);
        const seekTime = time / duration;
        wavesurfer.seekTo(seekTime);
      },
      playPause,
      play: () => {
        setIsPlaying(true);
        wavesurfer?.play();
      },
      pause: () => {
        setIsPlaying(false);
        wavesurfer?.pause();
      },
    }));

    return (
      <Stack direction="row" spacing={1.5} alignItems="center" flexGrow={1}>
        <LoadingButton
          variant="rounded"
          color="primary"
          onClick={playPause}
          loading={!isReady}
          sx={{
            '& .MuiLoadingButton-loadingIndicator': {
              color: theme.palette.text.primary,
            },
          }}
        >
          {isPlaying ? (
            <Iconify icon="tabler:player-pause-filled" />
          ) : (
            <Iconify icon="tabler:player-play-filled" />
          )}
        </LoadingButton>

        <Box flexGrow={1} ref={containerRef} />

        <Stack direction="row" minWidth={40}>
          <Typography variant="body2">
            <Timer startDate={timer} play={isPlaying} />
          </Typography>
        </Stack>

        <Button
          variant="contained"
          size="small"
          color="primary"
          sx={{
            minWidth: 0,
            width: 32,
            alignItems: 'baseline',
          }}
          onClick={changeSpeed}
        >
          {speed}
          <sub>x</sub>
        </Button>
      </Stack>
    );
  },
);
